import React, { useState, useRef, useEffect, useCallback } from 'react';
import jsQR from 'jsqr';
import { XMarkIcon, CameraIcon, QrCodeIcon } from '@heroicons/react/24/outline';

interface QRScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTagsAdded: (tags: string[]) => void;
}

interface DetectedCode {
  data: string;
  location: any;
  timestamp: number;
}

const QRScannerModal: React.FC<QRScannerModalProps> = ({ isOpen, onClose, onTagsAdded }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedTags, setScannedTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const activeDetectionsRef = useRef<Map<string, DetectedCode>>(new Map());
  const newlyAddedTagsRef = useRef<Map<string, number>>(new Map()); // Track when tags were newly added

  const stopCamera = useCallback(() => {
    setIsScanning(false);
    setCameraReady(false);
    setError(null);
    activeDetectionsRef.current.clear();
    newlyAddedTagsRef.current.clear();
    
    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const drawAllBoundingBoxes = useCallback(() => {
    if (!overlayCanvasRef.current || !videoRef.current || !cameraReady) return;
    
    const overlayCanvas = overlayCanvasRef.current;
    const video = videoRef.current;
    const ctx = overlayCanvas.getContext('2d');
    
    if (!ctx || !video.videoWidth || !video.videoHeight) return;
    
    // Set canvas size
    const rect = overlayCanvas.getBoundingClientRect();
    overlayCanvas.width = rect.width;
    overlayCanvas.height = rect.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    
    // Calculate scaling
    const scaleX = rect.width / video.videoWidth;
    const scaleY = rect.height / video.videoHeight;
    
    // Draw all active detections
    const now = Date.now();
    activeDetectionsRef.current.forEach((detection, tag) => {
      // Remove old detections
      if (now - detection.timestamp > 500) {
        activeDetectionsRef.current.delete(tag);
        return;
      }
      
      const isDuplicate = scannedTags.includes(tag);
      const { topLeftCorner, topRightCorner, bottomLeftCorner, bottomRightCorner } = detection.location;
      
      // Check if this tag was newly added within grace period (3 seconds)
      const addedTime = newlyAddedTagsRef.current.get(tag);
      const isNewlyAdded = addedTime && (now - addedTime < 3000);
      const showAsNew = !isDuplicate || isNewlyAdded;
      
      // Draw box
      ctx.strokeStyle = showAsNew ? '#16a34a' : '#dc2626';
      ctx.lineWidth = 4;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 4;
      
      ctx.beginPath();
      ctx.moveTo(topLeftCorner.x * scaleX, topLeftCorner.y * scaleY);
      ctx.lineTo(topRightCorner.x * scaleX, topRightCorner.y * scaleY);
      ctx.lineTo(bottomRightCorner.x * scaleX, bottomRightCorner.y * scaleY);
      ctx.lineTo(bottomLeftCorner.x * scaleX, bottomLeftCorner.y * scaleY);
      ctx.closePath();
      ctx.stroke();
      
      // Draw corners
      ctx.fillStyle = showAsNew ? '#16a34a' : '#dc2626';
      ctx.shadowBlur = 0;
      const markerSize = 10;
      
      [topLeftCorner, topRightCorner, bottomLeftCorner, bottomRightCorner].forEach(corner => {
        ctx.fillRect(
          corner.x * scaleX - markerSize/2, 
          corner.y * scaleY - markerSize/2, 
          markerSize, 
          markerSize
        );
      });
      
      // Draw text
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      const centerX = (topLeftCorner.x + bottomRightCorner.x) / 2 * scaleX;
      const centerY = (topLeftCorner.y + bottomRightCorner.y) / 2 * scaleY;
      
      // Background for text
      const text = showAsNew ? 'TAG ADDED' : 'ALREADY ADDED';
      const metrics = ctx.measureText(text);
      const padding = 8;
      
      ctx.fillStyle = showAsNew ? '#16a34a' : '#dc2626';
      ctx.fillRect(
        centerX - metrics.width/2 - padding,
        centerY - 10 - padding/2,
        metrics.width + padding * 2,
        20 + padding
      );
      
      ctx.fillStyle = 'white';
      ctx.fillText(text, centerX, centerY);
    });
  }, [cameraReady, scannedTags]);

  // Fast scanning with multiple strategies
  const frameCounter = useRef(0);
  
  const scanVideoFrame = useCallback(() => {
    if (!isScanning || !videoRef.current || !canvasRef.current || !cameraReady) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    
    if (!context || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationFrameRef.current = requestAnimationFrame(scanVideoFrame);
      return;
    }

    frameCounter.current++;
    
    // Full resolution for more accurate scanning
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const detectedInThisFrame = new Set<string>();
    
    // Define scanning area - simplified approach
    // Just scan a larger central area to ensure we catch QR codes in the visible square
    const scanRatio = 0.4; // 40% of the video frame (generous to account for aspect ratio differences)
    const scanSize = Math.min(canvas.width, canvas.height) * scanRatio;
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    const scanArea = {
      x: Math.floor(centerX - scanSize / 2),
      y: Math.floor(centerY - scanSize / 2),
      width: Math.ceil(scanSize),
      height: Math.ceil(scanSize)
    };
    
    // Ensure scan area is within bounds
    scanArea.x = Math.max(0, scanArea.x);
    scanArea.y = Math.max(0, scanArea.y);
    scanArea.width = Math.min(scanArea.width, canvas.width - scanArea.x);
    scanArea.height = Math.min(scanArea.height, canvas.height - scanArea.y);
    
    try {
      // Only scan within the center square area
      const imageData = context.getImageData(
        scanArea.x, 
        scanArea.y, 
        scanArea.width, 
        scanArea.height
      );
      
      const code = jsQR(imageData.data, scanArea.width, scanArea.height, {
        inversionAttempts: "dontInvert"
      });
      
      if (code && code.data && code.location) {
        const cleanTag = code.data.trim().toUpperCase();
        if (cleanTag.length === 12) {
          detectedInThisFrame.add(cleanTag);
          
          // Adjust location coordinates to full video frame
          const adjustedLocation = {
            topLeftCorner: { 
              x: code.location.topLeftCorner.x + scanArea.x, 
              y: code.location.topLeftCorner.y + scanArea.y 
            },
            topRightCorner: { 
              x: code.location.topRightCorner.x + scanArea.x, 
              y: code.location.topRightCorner.y + scanArea.y 
            },
            bottomLeftCorner: { 
              x: code.location.bottomLeftCorner.x + scanArea.x, 
              y: code.location.bottomLeftCorner.y + scanArea.y 
            },
            bottomRightCorner: { 
              x: code.location.bottomRightCorner.x + scanArea.x, 
              y: code.location.bottomRightCorner.y + scanArea.y 
            }
          };
          
          activeDetectionsRef.current.set(cleanTag, {
            data: cleanTag,
            location: adjustedLocation,
            timestamp: Date.now()
          });
          
          if (!scannedTags.includes(cleanTag)) {
            setScannedTags(prev => {
              if (prev.includes(cleanTag)) return prev;
              if ('vibrate' in navigator) navigator.vibrate(100);
              console.log('✅ New tag added:', cleanTag);
              // Record when this tag was newly added
              newlyAddedTagsRef.current.set(cleanTag, Date.now());
              return [...prev, cleanTag].sort().reverse();
            });
          }
        }
      }
    } catch (e) {
      console.error('QR scanning error:', e);
    }
    
    
    // Update timestamps for all currently visible tags
    detectedInThisFrame.forEach(tag => {
      const detection = activeDetectionsRef.current.get(tag);
      if (detection) {
        detection.timestamp = Date.now();
      }
    });
    
    // Clean up old detections more aggressively
    const now = Date.now();
    activeDetectionsRef.current.forEach((detection, tag) => {
      if (now - detection.timestamp > 300) {
        activeDetectionsRef.current.delete(tag);
      }
    });
    
    // Draw all bounding boxes
    drawAllBoundingBoxes();
    
    // Continue scanning immediately for faster response
    animationFrameRef.current = requestAnimationFrame(scanVideoFrame);
  }, [isScanning, cameraReady, scannedTags, drawAllBoundingBoxes]);

  const startCamera = async () => {
    try {
      setError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280, max: 1280 },
          height: { ideal: 720, max: 720 },
          frameRate: { ideal: 30, max: 30 }
        }
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play()
              .then(() => {
                setCameraReady(true);
                setIsScanning(true);
              })
              .catch(() => {
                setCameraReady(true);
                setIsScanning(true);
              });
          }
        };
      }
    } catch (error) {
      console.error('Camera error:', error);
      setError('Unable to access camera. Please check permissions.');
    }
  };

  // Start scanning when ready
  useEffect(() => {
    if (isScanning && cameraReady) {
      scanVideoFrame();
    }
  }, [isScanning, cameraReady, scanVideoFrame]);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setScannedTags([]);
      activeDetectionsRef.current.clear();
      newlyAddedTagsRef.current.clear();
    }
  }, [isOpen, stopCamera]);

  const removeTag = (tagToRemove: string) => {
    setScannedTags(prev => prev.filter(tag => tag !== tagToRemove));
    activeDetectionsRef.current.delete(tagToRemove);
    newlyAddedTagsRef.current.delete(tagToRemove);
  };

  const handleAddTags = () => {
    if (scannedTags.length > 0) {
      onTagsAdded(scannedTags);
      handleClose();
    }
  };

  const handleClose = () => {
    stopCamera();
    setScannedTags([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'white',
        zIndex: 9999,
        display: 'block',
        width: '100%',
        height: '100%'
      }}
    >
      {/* Header */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '60px',
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e5e5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px'
        }}
      >
        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>QR Code Scanner</h3>
        <button
          onClick={handleClose}
          style={{
            padding: '8px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          <XMarkIcon style={{ width: '24px', height: '24px' }} />
        </button>
      </div>

      {/* Fixed viewport area */}
      <div 
        style={{
          position: 'absolute',
          top: '60px',
          left: 0,
          right: 0,
          height: '320px', // Fixed height for viewport area
          padding: '16px 16px 0 16px'
        }}
      >
        {error && (
          <div 
            style={{
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              color: '#c00',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px'
            }}
          >
            {error}
          </div>
        )}

        {/* Camera container - smaller for better performance */}
        <div 
          style={{
            position: 'relative',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            overflow: 'hidden',
            marginBottom: '16px',
            height: '200px' // Fixed height
          }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: cameraReady ? 'block' : 'none'
            }}
          />
          
          {/* Scanning area overlay */}
          {cameraReady && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 10
              }}
            >
              {/* Scanning square in center */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '140px',
                  height: '140px',
                  transform: 'translate(-50%, -50%)',
                  border: '3px solid #16a34a',
                  borderRadius: '12px',
                  boxShadow: '0 0 0 2px rgba(22, 163, 74, 0.3), inset 0 0 0 2px rgba(22, 163, 74, 0.3)',
                  animation: isScanning ? 'pulse 2s infinite' : 'none'
                }}
              />
              
              {/* Corner indicators */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '140px',
                  height: '140px',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none'
                }}
              >
                {/* Top-left corner */}
                <div style={{
                  position: 'absolute',
                  top: '-3px',
                  left: '-3px',
                  width: '20px',
                  height: '20px',
                  borderTop: '6px solid #16a34a',
                  borderLeft: '6px solid #16a34a',
                  borderRadius: '12px 0 0 0'
                }} />
                
                {/* Top-right corner */}
                <div style={{
                  position: 'absolute',
                  top: '-3px',
                  right: '-3px',
                  width: '20px',
                  height: '20px',
                  borderTop: '6px solid #16a34a',
                  borderRight: '6px solid #16a34a',
                  borderRadius: '0 12px 0 0'
                }} />
                
                {/* Bottom-left corner */}
                <div style={{
                  position: 'absolute',
                  bottom: '-3px',
                  left: '-3px',
                  width: '20px',
                  height: '20px',
                  borderBottom: '6px solid #16a34a',
                  borderLeft: '6px solid #16a34a',
                  borderRadius: '0 0 0 12px'
                }} />
                
                {/* Bottom-right corner */}
                <div style={{
                  position: 'absolute',
                  bottom: '-3px',
                  right: '-3px',
                  width: '20px',
                  height: '20px',
                  borderBottom: '6px solid #16a34a',
                  borderRight: '6px solid #16a34a',
                  borderRadius: '0 0 12px 0'
                }} />
              </div>
              
              {/* Instruction text */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}
              >
                Center QR code in square
              </div>
            </div>
          )}
          
          {/* Overlay canvas for detection boxes */}
          <canvas
            ref={overlayCanvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              display: cameraReady ? 'block' : 'none',
              zIndex: 15
            }}
          />
          
          {/* Hidden canvas for QR processing */}
          <canvas
            ref={canvasRef}
            style={{ display: 'none' }}
          />
          
          {!cameraReady && (
            <div 
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center'
              }}
            >
              <CameraIcon style={{ width: '48px', height: '48px', color: '#999', margin: '0 auto 8px' }} />
              <p style={{ color: '#666', margin: '0' }}>Camera Preview</p>
              {!isScanning && (
                <p style={{ color: '#999', fontSize: '14px', marginTop: '8px' }}>
                  Tap "Start Camera" to begin
                </p>
              )}
            </div>
          )}
        </div>

        {/* Camera button */}
        <button
          onClick={isScanning ? stopCamera : startCamera}
          disabled={!!error}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: '8px',
            fontWeight: '500',
            color: 'white',
            border: 'none',
            backgroundColor: isScanning ? '#dc2626' : '#2563eb',
            opacity: error ? 0.5 : 1,
            cursor: error ? 'not-allowed' : 'pointer'
          }}
        >
          {isScanning ? 'Stop Camera' : 'Start Camera'}
        </button>
      </div>

      {/* Scrollable tags list area */}
      <div 
        style={{
          position: 'absolute',
          top: '380px', // Start below the fixed viewport area
          left: 0,
          right: 0,
          bottom: '80px', // Leave space for footer
          overflowY: 'auto',
          padding: '0 16px',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div>
          <h4 style={{ fontWeight: '500', marginBottom: '12px' }}>
            Scanned Tags ({scannedTags.length})
          </h4>
          
          {scannedTags.length === 0 ? (
            <div 
              style={{
                textAlign: 'center',
                padding: '32px',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px'
              }}
            >
              <QrCodeIcon style={{ width: '48px', height: '48px', color: '#999', margin: '0 auto 8px' }} />
              <p style={{ color: '#666', fontSize: '14px' }}>
                No tags scanned yet
              </p>
            </div>
          ) : (
            <div>
              {scannedTags.map((tag) => (
                <div
                  key={tag}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    marginBottom: '8px'
                  }}
                >
                  <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>{tag}</span>
                  <button
                    onClick={() => removeTag(tag)}
                    style={{
                      padding: '4px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#999'
                    }}
                  >
                    <XMarkIcon style={{ width: '16px', height: '16px' }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div 
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '80px',
          backgroundColor: 'white',
          borderTop: '1px solid #e5e5e5',
          padding: '16px',
          display: 'flex',
          gap: '12px'
        }}
      >
        <button
          onClick={handleClose}
          style={{
            flex: 1,
            padding: '8px 16px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontWeight: '500',
            color: '#374151',
            backgroundColor: 'white',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleAddTags}
          disabled={scannedTags.length === 0}
          style={{
            flex: 1,
            padding: '8px 16px',
            borderRadius: '8px',
            fontWeight: '500',
            color: 'white',
            border: 'none',
            backgroundColor: scannedTags.length > 0 ? '#2563eb' : '#d1d5db',
            cursor: scannedTags.length > 0 ? 'pointer' : 'not-allowed'
          }}
        >
          Add Tags to List
        </button>
      </div>
    </div>
  );
};

export default QRScannerModal;